/**
 * The pictures used for picture levels (every third numbered level; see
 * progression.ts), built into the game as data so levels can be made without
 * loading image files.
 *
 * The data lives in pictures.data.ts, generated from the PNG files in
 * public/images by scripts/make-pictures.mjs. Each picture is stored as its
 * list of colors plus one character per pixel; this file turns that back
 * into pixels and then into a game grid with `imageToGrid` (image.ts), the
 * same conversion used for any image.
 */
import { type ImageGrid, imageToGrid } from "./image";
import { ALPHABET, PICTURE_DATA } from "./pictures.data";

/** One picture as stored in pictures.data.ts. */
export interface PictureData {
	/** A short name, shown in the level title, e.g. "heart". */
	name: string;
	/** The PNG file in public/images it was made from. */
	file: string;
	width: number;
	height: number;
	/** The picture's distinct colors, each as "rrggbbaa" in hexadecimal. */
	colors: string[];
	/** One character per pixel, row after row: its color's position in ALPHABET, which indexes `colors`. */
	pixels: string;
}

/** A picture ready to play: its name, and the grid made from it. */
export interface Picture {
	name: string;
	grid: ImageGrid;
}

/** How many pictures there are. */
export const PICTURE_COUNT = PICTURE_DATA.length;

/** Converted grids, kept so each picture is only converted once. */
const cache = new Map<number, Picture>();

/**
 * The picture at position `index` (0 is the first and smallest), as a grid.
 * Throws if there's no such picture.
 */
export function picture(index: number): Picture {
	const cached = cache.get(index);
	if (cached) return cached;
	const data = PICTURE_DATA[index];
	if (!data) throw new Error(`No picture ${index}`);
	const colors = data.colors.map((hex) =>
		[0, 2, 4, 6].map((i) => Number.parseInt(hex.slice(i, i + 2), 16)),
	);
	const pixels = new Uint8Array(data.width * data.height * 4);
	for (let i = 0; i < data.pixels.length; i++) {
		const color = colors[ALPHABET.indexOf(data.pixels[i] as string)];
		if (color) pixels.set(color, i * 4);
	}
	const result = {
		name: data.name,
		grid: imageToGrid({ width: data.width, height: data.height, data: pixels }),
	};
	cache.set(index, result);
	return result;
}
